export type DeviceConnectivityState = 'online' | 'offline' | 'unknown' | 'stale';

export interface DeviceConnectivity {
  state: DeviceConnectivityState;
  online?: boolean;
  observedAt?: string;
  stale: boolean;
}

const ONLINE = new Set(['online', 'connected', 'reachable']);
const OFFLINE = new Set(['offline', 'disconnected', 'unreachable']);

const firstDefined = (...values: unknown[]): unknown => values.find((value) => value !== undefined && value !== null && value !== '');

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  const normalized = String(value ?? '').trim().toLowerCase();
  if (ONLINE.has(normalized)) return true;
  if (OFFLINE.has(normalized)) return false;
  return undefined;
};

/**
 * Normalize connectivity without treating lifecycle values such as `active`
 * as proof that a device is online.
 */
export function normalizeDeviceConnectivity(
  device: Record<string, any> | null | undefined,
  live?: Record<string, any> | null,
  options: { now?: number; staleAfterMs?: number } = {},
): DeviceConnectivity {
  const source = live || {};
  const online = parseBoolean(firstDefined(
    source.online,
    source.is_online,
    source.connection_status,
    source.connectionState,
    device?.online,
    device?.is_online,
    device?.connection_status,
    device?.connectionState,
  ));
  const observedAt = firstDefined(
    source.checked_at,
    source.fetched_at,
    source.last_checked_at,
    source.last_seen,
    source.last_seen_at,
    source.last_heartbeat,
    device?.last_checked_at,
    device?.last_seen,
    device?.last_seen_at,
    device?.last_sync_at,
  );
  const observed = typeof observedAt === 'string' ? observedAt : undefined;
  const observedMs = observed ? Date.parse(observed) : NaN;
  const now = options.now ?? Date.now();
  const staleAfterMs = options.staleAfterMs ?? 5 * 60 * 1000;
  const stale = Number.isFinite(observedMs) && now - observedMs > staleAfterMs;

  if (online === undefined) return { state: 'unknown', observedAt: observed, stale: false };
  if (stale) return { state: 'stale', online, observedAt: observed, stale: true };
  return { state: online ? 'online' : 'offline', online, observedAt: observed, stale: false };
}

export function summarizeDeviceConnectivity(
  devices: Record<string, any>[],
  liveById: Record<string, Record<string, any> | undefined> = {},
  options: { now?: number; staleAfterMs?: number } = {},
) {
  const summary = { online: 0, offline: 0, unknown: 0, stale: 0, total: devices.length };
  for (const device of devices) {
    const id = String(device.uuid || device.id || '');
    summary[normalizeDeviceConnectivity(device, liveById[id], options).state] += 1;
  }
  return summary;
}

